import * as React from "react";
import Head from "next/head";
import { AppProps } from "next/app";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { CacheProvider, EmotionCache } from "@emotion/react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { SnackbarProvider } from "notistack";
import { ConfirmProvider } from "material-ui-confirm";
import SendBird from "sendbird";
import "sendbird-uikit/dist/index.css";
import "../../styles/chat.css";

import theme from "../utils/theme";
import createEmotionCache from "../utils/createEmotionCache";
import { persistor, store } from "../app/store";
import { loadString, saveString } from "../utils/storage";
import { storageKeys, unprotectedRoutes } from "../constants";
import { sbConnect } from "../utils/sb";
import FullScreenLoader from "../components/common/FullScreenLoader";
import Script from "next/script";
import server from "../api";
import { useRouter } from "next/router";
import { useScrollRestoration } from "../hooks/useScrollRestoration";
import { ScrollContextProvider } from "../components/context/ScrollContext";
import GAScript from "../utils/GAScript";
import { MantineProvider } from "@mantine/core";
import { mantineTheme } from "../utils/mantineTheme";
import { createEmotionCache as createMantineEmotionCache } from "@mantine/core";

var sb = new SendBird({
  appId: process.env.NEXT_PUBLIC_SENDBIRD_APP_ID!,
  localCacheEnabled: true,
});

declare global {
  interface Window {
    Moengage: any;
  }
}

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();
export const mantineEmotion = createMantineEmotionCache({ key: "mantine" });

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

export default function MyApp(props: MyAppProps) {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const router = useRouter();
  useScrollRestoration(router);

  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const initAndConnectToSb = async () => {
      const userIdFromStorage = await loadString(storageKeys.USER_ID);
      if (userIdFromStorage) {
        const initResponse = await server.post("/v1/auth/initWeb", {
          fcmToken: "",
        });
        if (initResponse) {
          await saveString(
            storageKeys.USER_NAME,
            initResponse?.data?.user?.name
          );
          await saveString(
            storageKeys.USER_PROFILE_IMAGE,
            initResponse?.data?.user?.image
          );
        }
        await sbConnect(userIdFromStorage);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };
    initAndConnectToSb();
  }, []);

  if (unprotectedRoutes.includes(router.pathname)) {
    return (
      <ScrollContextProvider>
        <MantineProvider
          emotionCache={emotionCache}
          withGlobalStyles
          withNormalizeCSS
          theme={mantineTheme}
        >
          <CacheProvider value={emotionCache}>
            <Head>
              <meta
                name="viewport"
                content="initial-scale=1, width=device-width"
              />
            </Head>
            <ThemeProvider theme={theme}>
              {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
              <CssBaseline />
              <Component {...pageProps} />
            </ThemeProvider>
          </CacheProvider>
        </MantineProvider>
      </ScrollContextProvider>
    );
  }

  return (
    <ScrollContextProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <MantineProvider
            emotionCache={mantineEmotion}
            withGlobalStyles
            withNormalizeCSS
            theme={mantineTheme}
          >
            <CacheProvider value={emotionCache}>
              <Script
                strategy="afterInteractive"
                id="moengage"
                dangerouslySetInnerHTML={{
                  __html: `
                (function(i,s,o,g,r,a,m,n){i.moengage_object=r;t={};q=function(f){return function(){(i.moengage_q=i.moengage_q||[]).push({f:f,a:arguments})}};f=['track_event','add_user_attribute','add_first_name','add_last_name','add_email','add_mobile','add_user_name','add_gender','add_birthday','destroy_session','add_unique_user_id','moe_events','call_web_push','track','location_type_attribute'],h={onsite:["getData","registerCallback"]};for(k
                  in f){t[f[k]]=q(f[k])}for(k in h)for(l in h[k]){null==t[k]&&(t[k]={}),t[k][h[k][l]]=q(k+"."+h[k][l])}a=s.createElement(o);m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m);i.moe=i.moe||function(){n=arguments[0];return t};a.onload=function(){if(n){i[r]=moe(n)}}})(window,document,'script','https://cdn.moengage.com/webpush/moe_webSdk.min.latest.js','Moengage')
                  
                  Moengage = moe({
                    app_id: "${process.env.NEXT_PUBLIC_MOENGAGE_APP_ID}",
                    debug_logs: ${process.env.NODE_ENV === "production" ? 0 : 1}
                  });
                  `,
                }}
              ></Script>
              <Head>
                <meta
                  name="viewport"
                  content="initial-scale=1, width=device-width"
                />
                <meta
                  name="google-site-verification"
                  content="EUd8826EQFjD_zdf_iFDUhABGLNHFP9pOqdV5sCEeWE"
                />
              </Head>
              <GAScript />
              <noscript
                dangerouslySetInnerHTML={{
                  __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-MJMHMT9"
height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
                }}
              ></noscript>
              <ThemeProvider theme={theme}>
                <SnackbarProvider maxSnack={3}>
                  <ConfirmProvider>
                    {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                    <CssBaseline />
                    {isLoading ? (
                      <FullScreenLoader withHeader={false} />
                    ) : (
                      <Component {...pageProps} />
                    )}
                  </ConfirmProvider>
                </SnackbarProvider>
              </ThemeProvider>
            </CacheProvider>
          </MantineProvider>
        </PersistGate>
      </Provider>
    </ScrollContextProvider>
  );
}
